import { useCallback, useState, type ReactNode } from 'react';
import { Size, suggestionRowsFor, t, type SuggestionRow, type SuggestionSource } from '@bms2/shared';
import { Dropdown } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Modal } from '../../components/modals/Modal';
import { useAddNew } from './AddNewHost';
import { PAGES_PATH, PICKER_CAP, apiFetch, post, DEALS_PATH } from './api';

/** 🔨 Relative to the API's own root, never prefixed with /api: a Deal's links to its lists (decision 1093). */
export const BOM_LINKS_PATH = (dealId: string) => `${DEALS_PATH}/${dealId}/bom-links`;
export const BOM_LINK_PATH = (dealId: string, bomId: string) => `${DEALS_PATH}/${dealId}/bom-links/${bomId}`;

/** A picker's search over one list's rows, in the shared Dropdown's shape, leaving out the ids the caller already holds. */
function listSearch(recordType: 'BOM' | 'Deal', apiBaseUrl: string, leaveOut: readonly string[]) {
  return async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/${recordType}?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal }).catch(/* silent: a fetch the server never answered cannot reach the server's log either; the page says it could not reach the server. */ () => null);
    if (!response?.ok) return [];
    const body = (await response.json()) as { rows?: SuggestionSource[] };
    return suggestionRowsFor(recordType, (body.rows ?? []).filter((row) => !leaveOut.includes(String(row.id))));
  };
}

interface PickModalProps {
  title: string;
  label: string;
  entityLabel: string;
  dataModal: string;
  search: (query: string, signal: AbortSignal) => Promise<SuggestionRow[]>;
  onAddNew: () => void;
  /** Writes the pick; answers the server's refusal, or null where it saved. */
  write: (row: SuggestionRow) => Promise<string | null>;
  onCancel: () => void;
  onDone: () => void;
  addNewHost?: ReactNode;
}

/** One pick, written by the window's Attach; a refusal stays in the window in the server's own sentence. */
function PickModal({ title, label, entityLabel, dataModal, search, onAddNew, write, onCancel, onDone, addNewHost }: PickModalProps) {
  const [picked, setPicked] = useState<SuggestionRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const attach = async () => {
    if (!picked) return setError(t('deal.boms.pickFirst'));
    setError(null);
    setSaving(true);
    try {
      const refused = await write(picked);
      if (refused) return setError(refused);
      onDone();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title={title}
      onCancel={onCancel}
      dataModal={dataModal}
      actions={[
        { kind: 'primary', label: t('deal.boms.attach'), onAct: () => void attach(), disabled: saving },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <Dropdown size={Size.Large} label={label} entityLabel={entityLabel} search={search} minChars={0} value={picked?.line1 ?? null} onSelect={(row) => { setError(null); setPicked(row); }} onAddNew={onAddNew} disabled={saving} />
      {addNewHost}
    </Modal>
  );
}

/**
 * 🔒 Attach existing on the Deal's tile (decision 1093): a picker over every
 * list by name, leaving out the lists already on this Deal. Its Add New is the
 * tile's own Add, which makes a list and attaches it.
 */
export function AttachBomModal({ dealId, held, apiBaseUrl = '', onCancel, onAttached, onAddNew }: { dealId: string; held: readonly string[]; apiBaseUrl?: string; onCancel: () => void; onAttached: () => void; onAddNew: () => void }) {
  const search = useCallback((query: string, signal: AbortSignal) => listSearch('BOM', apiBaseUrl, held)(query, signal), [apiBaseUrl, held]);
  return (
    <PickModal
      title={t('deal.boms.attachExisting')}
      label={t('deal.boms.pickList')}
      entityLabel={t('deal.boms.entity')}
      dataModal="deal-bom-attach"
      search={search}
      onAddNew={onAddNew}
      write={async (row) => {
        const answer = await post(BOM_LINKS_PATH(dealId), { bomId: row.id }, apiBaseUrl);
        return answer.ok ? null : answer.message;
      }}
      onCancel={onCancel}
      onDone={onAttached}
    />
  );
}

/**
 * 🔒 Attach to a Deal on the list's page (decision 1093): a picker over the
 * Deals by name, leaving out the Deals the list is already on. Its Add New
 * opens a new Deal, which the list is then attached to.
 */
export function AttachToDealModal({ bomId, held, apiBaseUrl = '', onCancel, onAttached }: { bomId: string; held: readonly string[]; apiBaseUrl?: string; onCancel: () => void; onAttached: () => void }) {
  const addNew = useAddNew(apiBaseUrl);
  const [error, setError] = useState<string | null>(null);
  const search = useCallback((query: string, signal: AbortSignal) => listSearch('Deal', apiBaseUrl, held)(query, signal), [apiBaseUrl, held]);
  const write = async (row: SuggestionRow): Promise<string | null> => {
    const answer = await post(BOM_LINKS_PATH(row.id), { bomId }, apiBaseUrl);
    return answer.ok ? null : answer.message;
  };
  return (
    <>
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <PickModal
        title={t('deal.boms.attachToDeal')}
        label={t('deal.boms.pickDeal')}
        entityLabel={t('deal.boms.dealEntity')}
        dataModal="bom-attach-deal"
        search={search}
        onAddNew={() => addNew.open({ kind: 'record', recordType: 'Deal' }, (row) => void write(row).then((refused) => (refused ? setError(refused) : onAttached())))}
        write={write}
        onCancel={onCancel}
        onDone={onAttached}
        addNewHost={addNew.host}
      />
    </>
  );
}
